import {
    Box,
    Button,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Block, Delete, LocationOn } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useState } from "react"
import {
    cadastrarEnderecoBloqueado,
    excluirEnderecoBloqueado,
} from "../../../services/enderecosBloqueados"
import { EnderecosBloqueadosActionsTypes } from "../context/enderecosBloqueadosActions"
import { useEnderecosBloqueadosContext } from "../context/useEnderecosBloqueadosContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "900px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        boxShadow: "0 2px 8px rgba(var(--cor-botao-salvar-rgb),0.4)",
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
    },
    emptyText: {
        color: "#7f8c8d",
        textAlign: "center",
        padding: theme.spacing(3),
    },
}))

const formularioVazio = { cep: "", rua: "", numero: "", bairro: "", cidade: "" }

export function EnderecosBloqueadosForm() {
    const classes = useStyles()
    const { dados, dispatch, erroCarregamento } = useEnderecosBloqueadosContext()
    const { enqueueSnackbar } = useSnackbar()
    const [formulario, setFormulario] = useState(formularioVazio)
    const [salvando, setSalvando] = useState(false)

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    const handleChange =
        (campo: keyof typeof formularioVazio) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const valor = e.target.value
            setFormulario(prev => ({ ...prev, [campo]: valor }))
        }

    const handleCadastrar = async () => {
        if (!formulario.rua.trim()) {
            alertStart("O campo Rua é obrigatório.", "error")
            return
        }
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setSalvando(true)
        try {
            const response = await cadastrarEnderecoBloqueado(autorizacao, formulario)
            if ("retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao cadastrar endereço", "error")
            } else {
                dispatch({ type: EnderecosBloqueadosActionsTypes.CARREGAR, payload: response })
                setFormulario(formularioVazio)
                alertStart("Endereço bloqueado cadastrado com sucesso!", "success")
            }
        } finally {
            setSalvando(false)
        }
    }

    const handleExcluir = async (id: number) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        const response = await excluirEnderecoBloqueado(autorizacao, id)
        if (response && "retornoErro" in response) {
            alertStart(response.mensagem || "Erro ao excluir endereço", "error")
            return
        }
        dispatch({
            type: EnderecosBloqueadosActionsTypes.CARREGAR,
            payload: dados.lista.filter(e => e.id !== id),
        })
        alertStart("Endereço removido com sucesso!", "success")
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    Endereços Bloqueados
                </Typography>

                {/* Lista */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Block className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Endereços cadastrados
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        {dados.lista.length === 0 ? (
                            <Typography className={classes.emptyText}>
                                Nenhum endereço bloqueado cadastrado.
                            </Typography>
                        ) : (
                            <TableContainer style={{ overflowX: "auto" }}>
                                <Table size="small" style={{ minWidth: 700, width: "100%" }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ minWidth: 90 }}>CEP</TableCell>
                                            <TableCell style={{ minWidth: 180 }}>Rua</TableCell>
                                            <TableCell style={{ minWidth: 90 }}>Número</TableCell>
                                            <TableCell style={{ minWidth: 140 }}>Bairro</TableCell>
                                            <TableCell style={{ minWidth: 140 }}>Cidade</TableCell>
                                            <TableCell style={{ minWidth: 60 }} />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dados.lista.map(endereco => (
                                            <TableRow key={endereco.id}>
                                                <TableCell className={classes.nomeCell}>
                                                    {endereco.cep}
                                                </TableCell>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={endereco.rua}
                                                >
                                                    {endereco.rua}
                                                </TableCell>
                                                <TableCell>{endereco.numero}</TableCell>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={endereco.bairro}
                                                >
                                                    {endereco.bairro}
                                                </TableCell>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={endereco.cidade}
                                                >
                                                    {endereco.cidade}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    style={{ whiteSpace: "nowrap" }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleExcluir(endereco.id)}
                                                        title="Excluir"
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>

                {/* Formulário */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <LocationOn className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Cadastrar novo endereço bloqueado
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="CEP"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.cep}
                                    onChange={handleChange("cep")}
                                />
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <TextField
                                    label="Rua"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.rua}
                                    onChange={handleChange("rua")}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    label="Número"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.numero}
                                    onChange={handleChange("numero")}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Bairro"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.bairro}
                                    onChange={handleChange("bairro")}
                                />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    label="Cidade"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.cidade}
                                    onChange={handleChange("cidade")}
                                />
                            </Grid>
                            <Grid item xs={12} style={{ textAlign: "center" }}>
                                <Button
                                    className={classes.saveButton}
                                    variant="contained"
                                    onClick={handleCadastrar}
                                    disabled={salvando}
                                >
                                    {salvando ? "Salvando..." : "Salvar"}
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
